import { canReadDomain } from '@/api/core/authorization';
import type { EzremsTransitionCommand } from '@/api/core/ezrems-transition-command';
import {
  readEzremsUnitTransitionPreview,
  readEzremsUnitTransitionStatus,
  submitEzremsUnitTransition,
  type EzremsUnitTransitionPreview,
  type EzremsUnitTransitionStatus,
} from '@/api/core/ezrems-unit-transition';
import type { ServiceClient } from '@/api/http/client';
import { isApiError } from '@/api/http/errors';
import { isUuid } from '@/lib/validation';
import {
  Button,
  ConfirmDialog,
  DatePicker,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@mgrvtechgroup/admin-ds';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import {
  beforeFirstDateBound,
  firstDateBound,
  firstDateBoundMessage,
} from './first-date-lower-bound';
import {
  firstDateRefusal,
  missingStatusText,
  transitionCommandMissing,
  transitionNotAccepted,
  transitionRefusalText,
} from './first-date-refusal';
import type { PropertyRuntime } from './property-queries';

/**
 * 슬롯(Unit) 단위 EZREMS 판매 전환. 로컬 BE candidate(010, 미배포) 계약을 따른다.
 * 접수(202)는 완료가 아니다 — 같은 슬롯·같은 원 command UUID의 상태 조회로만 결과를 확정하고,
 * 결과가 불명확하면 새 UUID나 자동 재전송으로 우회하지 않는다.
 */
interface Props {
  runtime: PropertyRuntime;
  generation: number;
  roomId: string;
  roomName: string;
  unit: { id: string; name: string };
  onApplied?: () => void;
  /** 이 슬롯의 보존 요청이 생기거나 사라졌을 때 알린다(다른 슬롯 화면의 안내 갱신용). */
  onHeldChange?: () => void;
  /** 아직 접수하지 않은 입력이 있는지 알린다(화면을 떠날 때 경고하는 데 쓴다). */
  onDirtyChange?: (dirty: boolean) => void;
}
interface Intent {
  namespace: string;
  generation: number;
  unitId: string;
  unitName: string;
  roomName: string;
  actorId: string;
  preview: EzremsUnitTransitionPreview;
  command: EzremsTransitionCommand;
}
// 메모리에만 둔다: 화면을 다시 그려도 결과 불명 요청을 잃지 않는다.
// 서버의 미해결 판매 요청(pending)은 슬롯마다 따로이므로 보존도 슬롯 단위다 — 한 슬롯의 불명 요청이 다른 슬롯을 막지 않는다.
const retainedByClient = new WeakMap<ServiceClient, Map<string, Intent>>();
const retained = {
  get: (client: ServiceClient, unitId: string) => retainedByClient.get(client)?.get(unitId),
  set: (client: ServiceClient, intent: Intent) => {
    let held = retainedByClient.get(client);
    if (!held) {
      held = new Map();
      retainedByClient.set(client, held);
    }
    held.set(intent.unitId, intent);
  },
  delete: (client: ServiceClient, unitId: string) => retainedByClient.get(client)?.delete(unitId),
};
/**
 * 결과 확인이 필요한 슬롯 요청의 대상. 같은 세션(namespace·generation)·권한일 때만 이름을 알려 주고,
 * 그 밖에는 이름 없이 개수만 알려 개인·업무 정보를 노출하지 않는다.
 */
export function pendingUnitTransitions(runtime: PropertyRuntime, generation: number) {
  const held = [...(retainedByClient.get(runtime.client)?.values() ?? [])].filter(
    (i) => i.namespace === runtime.apiNamespace,
  );
  const visible = access({ runtime, generation });
  return {
    count: held.length,
    items: visible
      ? held
          .filter((i) => i.generation === generation)
          .map((i) => ({ unitId: i.unitId, unitName: i.unitName, roomName: i.roomName }))
      : [],
  };
}
const access = ({ runtime, generation }: Pick<Props, 'runtime' | 'generation'>) => {
  const a = runtime.session.getSnapshot();
  return (
    a.generation === generation &&
    a.status === 'authorized' &&
    a.authorization !== null &&
    !a.deniedDomains.includes('booking') &&
    canReadDomain(a.authorization, 'booking', 32)
  );
};
const contractLabels = {
  UNKNOWN: '미확인',
  UNBOUND: '슬롯 연결 필요',
  OBSERVED_NO_FUTURE: '후속 계약 없음 확인',
  FUTURE_CONTRACT: '후속 계약 있음',
  DRAFT_CONTRACT: '후속 계약 작성 중',
};
const product = (v: string | null) =>
  v === 'midterm' ? '미드텀' : v === 'long_term' ? '일반 판매' : '미설정';
const unavailableReason = (reason: string | null) =>
  reason === 'UNBOUND'
    ? '이 슬롯은 아직 EZREMS 호실에 연결되지 않았습니다. 위 슬롯 연결에서 먼저 연결하세요.'
    : reason === 'UNSUPPORTED_TARGET'
      ? '이 슬롯은 EZREMS 판매 전환을 지원하지 않습니다.'
      : '지금은 이 슬롯의 EZREMS 판매 전환을 쓸 수 없습니다.';
const eligible = (
  d: EzremsUnitTransitionPreview,
  c: Pick<EzremsTransitionCommand, 'target' | 'first_move_in_date'>,
) =>
  d.write_supported &&
  !d.pending &&
  (c.target === 'GENERAL' ||
    (d.eligible &&
      d.contract_status !== 'UNKNOWN' &&
      !!c.first_move_in_date &&
      !beforeFirstDateBound(firstDateBound(d), c.first_move_in_date)));

export function EzremsUnitTransitionSection(props: Props) {
  const auth = useSyncExternalStore(
    props.runtime.session.subscribe,
    props.runtime.session.getSnapshot,
    props.runtime.session.getSnapshot,
  );
  const key = JSON.stringify([props.runtime.apiNamespace, props.generation, props.unit.id, auth]);
  return <Transition key={key} {...props} />;
}

function Transition(props: Props) {
  const { runtime, generation } = props;
  const [data, setData] = useState<EzremsUnitTransitionPreview | null>(null);
  const [held, setHeld] = useState<Intent | null>(
    () => retained.get(runtime.client, props.unit.id) ?? null,
  );
  const [reviewed, setReviewed] = useState<Intent | null>(null);
  const [status, setStatus] = useState<EzremsUnitTransitionStatus | null>(null);
  const [target, setTarget] = useState<'MIDTERM' | 'GENERAL'>('MIDTERM');
  const [date, setDate] = useState('');
  const [dailyPrice, setDailyPrice] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState('');
  const alive = useRef(true);
  const lock = useRef(false);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const priceGiven = dailyPrice.trim() !== '';
  const priceValid = /^[1-9]\d*$/.test(dailyPrice) && Number.isSafeInteger(Number(dailyPrice));
  useEffect(() => {
    props.onHeldChange?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 보존 요청이 바뀔 때만 알린다.
  }, [held]);
  const dirty = !!data && !held && (reason !== '' || dailyPrice !== '' || date !== '');
  useEffect(() => {
    props.onDirtyChange?.(dirty);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 입력이 바뀔 때만 알린다.
  }, [dirty]);
  useEffect(
    () => () => {
      props.onDirtyChange?.(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 화면이 사라질 때만 해제한다.
    [],
  );
  const valid = () => alive.current && access(props);
  const scope = (d: EzremsUnitTransitionPreview) => {
    if (d.room_id.toLowerCase() !== props.roomId.toLowerCase()) throw new Error('Room changed');
  };
  const actor = async () => {
    const a = await runtime.client.get<{ user_id?: unknown }>('users/me', {
      headers: { 'Cache-Control': 'no-store' },
    });
    if (!valid() || !isUuid(a.user_id)) throw new Error('Actor unavailable');
    return a.user_id;
  };
  const fail = (e: unknown, text = '슬롯 계약·전환 준비를 확인하지 못했습니다.') => {
    if (!alive.current) return;
    setFeedback(
      isApiError(e) && e.status === 404
        ? '슬롯 전환 정보를 찾을 수 없습니다. 이 서버가 슬롯 단위 전환을 지원하는지 확인하세요.'
        : text,
    );
    if (isApiError(e) && e.status === 403)
      runtime.session.reportDomainAccessDenied('booking', generation);
  };
  const read = async () => {
    if (!valid() || lock.current) return;
    lock.current = true;
    setBusy(true);
    setData(null);
    setReviewed(null);
    setFeedback('');
    try {
      const d = await readEzremsUnitTransitionPreview(runtime.client, props.unit.id);
      scope(d);
      if (valid()) setData(d);
    } catch (e) {
      fail(e);
    } finally {
      lock.current = false;
      if (alive.current) setBusy(false);
    }
  };
  const review = async () => {
    if (!valid() || lock.current || held || !data) return;
    lock.current = true;
    setBusy(true);
    setFeedback('');
    try {
      const actorId = await actor();
      const fresh = await readEzremsUnitTransitionPreview(runtime.client, props.unit.id);
      scope(fresh);
      if (!valid() || (await actor()) !== actorId) throw new Error('Actor changed');
      if (JSON.stringify(fresh) !== JSON.stringify(data)) {
        setData(fresh);
        throw new Error('Preview changed');
      }
      const command: EzremsTransitionCommand = {
        command_id: crypto.randomUUID(),
        target,
        expected_sale_revision: fresh.sale_revision,
        first_move_in_date: target === 'MIDTERM' ? date : null,
        ...(target === 'MIDTERM' && priceGiven ? { daily_price_minor: Number(dailyPrice) } : {}),
        reason,
      };
      if ((target === 'MIDTERM' && priceGiven && !priceValid) || !eligible(fresh, command))
        throw new Error('Unavailable');
      setReviewed({
        namespace: runtime.apiNamespace,
        generation,
        unitId: props.unit.id,
        unitName: props.unit.name,
        roomName: props.roomName,
        actorId,
        preview: fresh,
        command,
      });
    } catch (e) {
      fail(e, '슬롯 전환 내용이 바뀌었거나 조건을 충족하지 못했습니다. 다시 조회해 확인하세요.');
    } finally {
      lock.current = false;
      if (alive.current) setBusy(false);
    }
  };
  const owns = (i: Intent) =>
    valid() &&
    i.namespace === runtime.apiNamespace &&
    i.generation === generation &&
    i.unitId === props.unit.id;
  // 상태 조회는 읽기뿐이라, 같은 namespace·같은 수행자(users/me로 확인)이면 로그인 generation이 바뀐 뒤에도 허용한다.
  const ownsRead = (i: Intent) =>
    valid() && i.namespace === runtime.apiNamespace && i.unitId === props.unit.id;
  const submit = async (i: Intent, replay: boolean) => {
    if (!owns(i) || lock.current) return;
    lock.current = true;
    setBusy(true);
    setFeedback('');
    setStatus(null);
    let accepted = false;
    try {
      if ((await actor()) !== i.actorId) throw new Error('Actor changed');
      if (!replay) {
        const fresh = await readEzremsUnitTransitionPreview(runtime.client, i.unitId);
        scope(fresh);
        if ((await actor()) !== i.actorId || !owns(i)) throw new Error('Actor changed');
        if (JSON.stringify(fresh) !== JSON.stringify(i.preview) || !eligible(fresh, i.command)) {
          setData(fresh);
          setReviewed(null);
          throw new Error('Changed before submit');
        }
        retained.set(runtime.client, i);
        setHeld(i);
        setReviewed(null);
      }
      await submitEzremsUnitTransition(runtime.client, i.unitId, i.command);
      accepted = true;
      if (owns(i))
        setFeedback(
          '판매 요청 접수 응답을 확인했습니다. 최종 반영은 판매 요청 상태를 조회해 확인하세요.',
        );
    } catch (e) {
      // 새 요청이 서버에서 접수 전 거절(409 제외 4xx)되었으면 보존을 풀고, 409·그 외에는 명령이 남았을 수 있어 결과 불명으로 보존한다.
      const definitive = !replay && !accepted && transitionNotAccepted(e);
      if (definitive && retained.get(runtime.client, i.unitId) === i) {
        retained.delete(runtime.client, i.unitId);
        setHeld(null);
      }
      const refusal = !replay && !accepted ? firstDateRefusal(e) : null;
      fail(
        e,
        definitive
          ? '서버가 요청을 거절했습니다. 입력과 권한을 확인하세요.'
          : refusal !== null && retained.get(runtime.client, i.unitId) === i
            ? transitionRefusalText(refusal, { scope: 'unit', context: 'held' })
            : '접수 결과가 불명확합니다. 처음 요청을 보존했으며 자동 재전송하지 않습니다.',
      );
    } finally {
      lock.current = false;
      if (alive.current) setBusy(false);
    }
  };
  const inspect = async (i: Intent) => {
    if (!ownsRead(i) || lock.current) return;
    lock.current = true;
    setBusy(true);
    setFeedback('');
    setStatus(null);
    // 404를 "원명령 없음"으로 읽는 것은 상태 조회 호출 자체에 한정한다(뒤따른 preview의 404와 구분).
    const statusNotFound = { value: false };
    try {
      if ((await actor()) !== i.actorId) throw new Error('Actor changed');
      const result = await readEzremsUnitTransitionStatus(
        runtime.client,
        i.unitId,
        i.command.command_id,
        i.command.target,
      ).catch((statusError: unknown) => {
        statusNotFound.value = transitionCommandMissing(statusError);
        throw statusError;
      });
      if ((await actor()) !== i.actorId || !ownsRead(i)) throw new Error('Actor changed');
      setStatus(result);
      if ((result.outcome === 'APPLIED' || result.outcome === 'NOT_APPLIED') && !result.pending) {
        const fresh = await readEzremsUnitTransitionPreview(runtime.client, i.unitId);
        scope(fresh);
        if ((await actor()) !== i.actorId || !ownsRead(i)) throw new Error('Actor changed');
        if (
          fresh.pending ||
          fresh.sale_revision !== result.current_sale_revision ||
          retained.get(runtime.client, i.unitId) !== i
        )
          throw new Error('Current sale changed');
        if (
          result.outcome === 'APPLIED' &&
          (result.readback_matches !== true ||
            fresh.sale_product !== (i.command.target === 'MIDTERM' ? 'midterm' : 'long_term'))
        )
          throw new Error('Transition readback mismatch');
        retained.delete(runtime.client, i.unitId);
        setHeld(null);
        setData(fresh);
        setReviewed(null);
        // 같은 명령의 상태와 다시 읽은 현재 판매가 일치해 요청이 끝난 것이 확정됐다 — 그 요청의 초안은 더 이상 미저장 입력이 아니다.
        setReason('');
        setDailyPrice('');
        setDate('');
        if (result.outcome === 'APPLIED') props.onApplied?.();
      }
    } catch (e) {
      if (statusNotFound.value) {
        // 404는 미실행의 증거가 아니다(대상 불일치·commit 전 경합 가능). 보존한 채 읽기로만 권한·대상·preview를 다시 맞춘다.
        let state: Parameters<typeof missingStatusText>[0] = { reread: false };
        try {
          const latest = await readEzremsUnitTransitionPreview(runtime.client, i.unitId);
          scope(latest);
          if ((await actor()) === i.actorId && ownsRead(i)) {
            setData(latest);
            state = {
              reread: true,
              trace:
                !latest.pending && latest.sale_revision === i.command.expected_sale_revision
                  ? 'none'
                  : 'changed',
            };
          }
        } catch (readError) {
          if (isApiError(readError) && readError.status === 403)
            runtime.session.reportDomainAccessDenied('booking', generation);
        }
        if (alive.current)
          setFeedback(missingStatusText(state, i.generation === generation && allowed));
        return;
      }
      fail(
        e,
        '판매 요청 상태를 확정하지 못했습니다. 처음 요청을 보존했습니다. 상태를 다시 조회하세요.',
      );
    } finally {
      lock.current = false;
      if (alive.current) setBusy(false);
    }
  };
  const allowed = access(props);
  const ownHeld = held?.namespace === runtime.apiNamespace && held.unitId === props.unit.id;
  const sameSession = ownHeld && held.generation === generation;
  const blocked = busy || !!held || !!reviewed;
  const blockedReason = (() => {
    if (!data) return '슬롯 계약·전환 준비를 먼저 조회하세요.';
    if (!data.write_supported) return unavailableReason(data.write_unavailable_reason);
    if (data.pending) return '이전 판매 요청의 결과를 먼저 확인하세요.';
    if (target === 'MIDTERM') {
      if (data.contract_status === 'UNKNOWN')
        return '계약 상태를 확인해야 중기 판매로 전환할 수 있습니다.';
      if (!data.eligible) return '계약·준비일 조건을 충족하지 못했습니다.';
      if (!date) return '입주 가능 첫날을 선택하세요.';
      if (beforeFirstDateBound(firstDateBound(data), date))
        return '선택한 날짜가 너무 이릅니다. 위 안내의 가장 이른 날부터 선택할 수 있습니다.';
      if (priceGiven && !priceValid) return '일가격은 양의 정수 원 단위로 입력하세요.';
    }
    if (!reason.trim()) return '전환 사유를 입력하세요.';
    return '전환 내용을 검토할 수 있습니다.';
  })();
  return (
    <section
      aria-label="슬롯 계약과 외부 전환 준비"
      className="body-sm space-y-4 rounded-md border p-4 leading-relaxed"
    >
      <h4 className="font-semibold">
        {props.roomName} · {props.unit.name} 슬롯 EZREMS 전환
      </h4>
      <p>
        슬롯 단위로 중기 판매 또는 일반 판매 복귀를 검토합니다. 접수 응답은 완료가 아니며, 같은
        요청의 상태 조회로만 결과를 확인합니다.
      </p>
      <Button
        className="h-auto min-h-8 whitespace-normal"
        variant="outline"
        disabled={!allowed || busy}
        onClick={() => void read()}
      >
        {feedback && !data && !held ? '슬롯 다시 조회' : '슬롯 계약·전환 준비 조회'}
      </Button>
      {!allowed && <p>슬롯 전환 조회에 Booking 판매 권한이 필요합니다.</p>}
      {!data && (
        <p role="status">
          {busy ? '슬롯 계약·전환 준비 조회 중' : '현재·후속 계약 및 입주 가능 첫날: 미확인'}
        </p>
      )}
      {data && (
        <>
          <div role="status" className="space-y-2">
            <p>계약 상태: {contractLabels[data.contract_status]}</p>
            <p>계약·준비일 판단: {data.eligible ? '충족' : '미충족'}</p>
            <p>
              최종 계약 종료일: {data.current_end ?? '미확인'} · 실제 예약 가능 첫날:{' '}
              {data.earliest_date ?? '미확인'}
            </p>
            {!data.write_supported && <p>{unavailableReason(data.write_unavailable_reason)}</p>}
            <p>
              미해결 판매 요청: {data.pending ? '있음' : '없음'} · 현재 판매:{' '}
              {product(data.sale_product)} · 객실 전체 슬롯 {data.unit_ids.length}개
            </p>
          </div>
          <Select
            value={target}
            disabled={blocked}
            onValueChange={(v) => {
              if (v === 'MIDTERM' || v === 'GENERAL') setTarget(v);
            }}
            items={[
              { value: 'MIDTERM', label: '미드텀 판매' },
              { value: 'GENERAL', label: '일반 판매 복귀' },
            ]}
          >
            <SelectTrigger aria-label="슬롯 외부 판매 대상" className="w-full sm:max-w-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="MIDTERM">미드텀 판매</SelectItem>
              <SelectItem value="GENERAL">일반 판매 복귀</SelectItem>
            </SelectContent>
          </Select>
          {target === 'MIDTERM' && (
            <fieldset disabled={blocked} className="grid gap-3 sm:grid-cols-2">
              <legend className="mb-3 font-medium">슬롯 미드텀 전환 설정</legend>
              <label className="grid gap-1">
                전환 일가격 (원, 선택)
                <Input
                  aria-label="슬롯 전환 일가격 (원)"
                  inputMode="numeric"
                  value={dailyPrice}
                  onChange={(e) => {
                    setDailyPrice(e.target.value);
                  }}
                />
              </label>
              <div className="grid gap-1">
                <p>입주 가능 첫날</p>
                <DatePicker
                  value={date ? new Date(`${date}T00:00:00`) : undefined}
                  onChange={(value) => {
                    setDate(
                      value
                        ? `${String(value.getFullYear())}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`
                        : '',
                    );
                  }}
                />
              </div>
              <p className="text-muted-foreground sm:col-span-2">
                {firstDateBoundMessage(firstDateBound(data))}
              </p>
              <p className="text-muted-foreground sm:col-span-2">
                일가격을 비우면 이 슬롯에 설정된 가격을 그대로 쓰며, 설정이 없으면 서버가 거절할 수
                있습니다. 보증금·청소비는 객실 유형·객실에서 상속된 값이 적용됩니다.
              </p>
            </fieldset>
          )}
          <label className="grid gap-1">
            슬롯 전환 사유
            <Input
              aria-label="슬롯 전환 사유"
              value={reason}
              disabled={blocked}
              maxLength={500}
              onChange={(e) => {
                setReason(e.target.value);
              }}
            />
          </label>
          {!held && (
            <>
              <p id="ezrems-unit-transition-block" role="status">
                {blockedReason}
              </p>
              <Button
                aria-describedby="ezrems-unit-transition-block"
                className="h-auto min-h-8 whitespace-normal"
                disabled={
                  !allowed ||
                  blocked ||
                  (target === 'MIDTERM' && priceGiven && !priceValid) ||
                  !reason.trim() ||
                  Array.from(reason).length > 500 ||
                  !eligible(data, { target, first_move_in_date: date })
                }
                onClick={() => void review()}
              >
                슬롯 전환 접수 검토
              </Button>
            </>
          )}
        </>
      )}
      <ConfirmDialog
        open={!!reviewed}
        onOpenChange={(open) => {
          if (!open) setReviewed(null);
        }}
        target={`${props.roomName} · ${props.unit.name} 슬롯`}
        action="슬롯 전환 접수"
        consequence={`${reviewed?.command.target === 'GENERAL' ? '일반 판매 복귀' : '미드텀 판매'} · 입주 가능 첫날 ${reviewed?.command.first_move_in_date ?? '해당 없음'} · ${reviewed?.command.daily_price_minor ? `일가격 ${reviewed.command.daily_price_minor.toLocaleString('ko-KR')}원` : '슬롯 가격 설정 유지'} · 사유 ${reviewed?.command.reason ?? ''}. 이 슬롯만 바뀌며 같은 객실의 다른 슬롯은 바뀌지 않습니다. 기존 예약·결제는 유지됩니다.`}
        variant="default"
        onConfirm={() => {
          if (reviewed) void submit(reviewed, false);
        }}
      />
      {held && (
        <>
          <p role="alert">
            원래 슬롯 판매 요청을 보존했습니다. 새 요청은 차단하고 자동 재전송하지 않습니다.
            {sameSession && allowed && (
              <>
                {' '}
                대상: {held.unitName} 슬롯 · 사유: {held.command.reason}.
              </>
            )}
          </p>
          {ownHeld && !sameSession && (
            <p role="status">
              이전 로그인에서 접수한 요청입니다. 같은 수행자로 확인되면 상태를 조회할 수 있고, 같은
              요청을 다시 보내는 것은 현재 로그인에서는 막혀 있습니다.
            </p>
          )}
          {ownHeld && (
            <div className="flex flex-wrap items-center gap-2">
              {sameSession && (
                <Button
                  className="h-auto min-h-8 whitespace-normal"
                  disabled={!allowed || busy}
                  onClick={() => void submit(held, true)}
                >
                  같은 요청 다시 시도
                </Button>
              )}
              <Button
                className="h-auto min-h-8 whitespace-normal"
                disabled={!allowed || busy}
                onClick={() => void inspect(held)}
              >
                슬롯 판매 요청 상태 조회
              </Button>
            </div>
          )}
        </>
      )}
      {status && (
        <p role="status">
          슬롯 판매 요청 결과:{' '}
          {
            {
              PENDING: '확인 대기',
              UNKNOWN: '미확인',
              APPLIED: status.pending ? '확인 대기' : '반영 확인',
              NOT_APPLIED: '미반영 확인',
            }[status.outcome]
          }{' '}
          · 전송: {status.transport === 'DELIVERED' ? '전달 확인' : '미확인'} · 대상 상태 비교:{' '}
          {status.readback_matches === null
            ? '미확인'
            : status.readback_matches
              ? '일치'
              : '불일치'}
          {status.reason ? ` · 서버 사유 ${status.reason}` : ''}
        </p>
      )}
      {feedback && <p role="status">{feedback}</p>}
      {(held !== null || status !== null) && (
        <p>
          접수 응답이나 대상 상태 일치만으로는 외부 최종 성공을 확인할 수 없습니다. 판매 요청 결과를
          확인하세요.
        </p>
      )}
    </section>
  );
}
